import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Seo } from "@/components/seo/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CTAGroup } from "@/components/CTAGroup";
import { FAQAccordion } from "@/components/FAQAccordion";
import { BRAND, BLOG_POSTS } from "@/data/site";
import { articleSchema, breadcrumbSchema, faqSchema, orgSchema, serviceSchema } from "@/lib/schema";
import { supabase } from "@/integrations/supabase/client";
import { RichText, stripMarkdown } from "@/components/RichText";

type Section = { h: string; p: string[]; bullets?: string[]; table?: { headers: string[]; rows: string[][] } };
type Faq = { q: string; a: string };
type Body = { intro: string; sections: Section[]; faqs: Faq[]; heroUrl?: string | null; heroAlt?: string | null };

const STATIC_BODIES: Record<string, Body> = {};

type LoadedPost = {
  slug: string; title: string; excerpt: string; category: string; readTime: string;
  date: string; body: Body; metaTitle?: string | null; metaDescription?: string | null;
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState<LoadedPost | null | undefined>(undefined);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      const { data } = await supabase
        .from("blog_posts")
        .select("slug,title,excerpt,category,read_time,meta_title,meta_description,intro,sections,faqs,published_at,status,hero_image_url,hero_image_alt" as any)
        .eq("slug", slug)
        .eq("status", "published")
        .maybeSingle();
      if (data) {
        const d: any = data;
        setPost({
          slug: d.slug, title: d.title, excerpt: d.excerpt,
          category: d.category, readTime: d.read_time,
          metaTitle: d.meta_title ?? null,
          metaDescription: d.meta_description ?? null,
          date: (d.published_at ?? new Date().toISOString()).slice(0, 10),
          body: {
            intro: d.intro,
            sections: (d.sections as any) ?? [],
            faqs: (d.faqs as any) ?? [],
            heroUrl: d.hero_image_url ?? null,
            heroAlt: d.hero_image_alt ?? null,
          },
        });
        return;
      }
      const stat = BLOG_POSTS.find((p) => p.slug === slug);
      const body = STATIC_BODIES[slug];
      if (stat && body) {
        setPost({ slug: stat.slug, title: stat.title, excerpt: stat.excerpt, category: stat.category, readTime: stat.readTime, date: stat.date, body });
      } else {
        setPost(null);
      }
    })();
  }, [slug]);

  if (post === undefined) return <Layout><div className="container-page py-16">Loading…</div></Layout>;
  if (post === null) return <Navigate to="/blog" replace />;

  const url = `${BRAND.website}/blog/${post.slug}`;
  return (
    <Layout>
      <Seo
        title={stripMarkdown(post.metaTitle ?? post.title)}
        description={stripMarkdown(post.metaDescription ?? post.excerpt)}
        canonical={url}
        type="article"
        jsonLd={[
          orgSchema(),
          articleSchema({ title: post.title, description: post.metaDescription ?? post.excerpt, date: post.date, url, image: post.body.heroUrl ?? undefined }),
          ...(post.slug === "luxury-vinyl-plank-vs-hardwood-flooring" ? [
            serviceSchema("Luxury Vinyl Plank Installation", "Professional luxury vinyl plank installation for Connecticut homes.", `${BRAND.website}/services/luxury-vinyl-plank`),
            serviceSchema("Hardwood Flooring Installation", "Professional solid and engineered hardwood flooring installation in Connecticut.", `${BRAND.website}/services/hardwood-flooring`),
          ] : []),
          ...(post.slug === "how-to-prepare-for-flooring-installation" ? [
            serviceSchema("Flooring Installation", "Professional flooring installation and preparation for Connecticut homes.", `${BRAND.website}/services`),
          ] : []),
          ...(post.slug === "fall-hardwood-refinishing-connecticut" ? [
            serviceSchema("Hardwood Floor Refinishing", "Dustless hardwood floor sanding and refinishing for Connecticut homes.", `${BRAND.website}/services/floor-refinishing`),
          ] : []),
          ...(post.slug === "lvp-flooring-pets-connecticut" ? [
            serviceSchema("Luxury Vinyl Plank Installation", "Pet-friendly, waterproof luxury vinyl plank installation for Connecticut homes.", `${BRAND.website}/services/luxury-vinyl-plank`),
          ] : []),
          faqSchema(post.body.faqs),
          breadcrumbSchema([
            { name: "Home", url: BRAND.website },
            { name: "Blog", url: `${BRAND.website}/blog` },
            { name: post.title, url },
          ]),
        ]}
      />
      <article className="container-page py-12 md:py-16 max-w-3xl">
        <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Blog", href: "/blog" }, { name: post.category }]} />
        <div className="mt-6 mb-8">
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wider text-accentInk mb-3">
            <span>{post.category}</span><span className="text-muted-foreground">· {post.readTime}</span>
            <span className="text-muted-foreground">· {new Date(post.date + "T12:00:00").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4 leading-tight">{stripMarkdown(post.title)}</h1>
          <p className="text-muted-foreground text-lg">{stripMarkdown(post.excerpt)}</p>
        </div>
        {post.body.heroUrl && (
          <figure className="mb-10 -mx-4 md:mx-0">
            <img
              src={post.body.heroUrl}
              alt={post.body.heroAlt ?? post.title}
              className="w-full aspect-[16/9] object-cover rounded-lg border border-border"
              loading="eager"
            />
          </figure>
        )}
        <div className="prose prose-lg max-w-none">
          <p className="text-foreground text-xl leading-relaxed mb-2">
            <RichText text={post.body.intro} />
          </p>
          {post.body.sections.map((s, idx) => (
            <section key={idx} className="mt-10">
              <h2 className="font-display text-2xl md:text-3xl font-bold mb-4">{stripMarkdown(s.h)}</h2>
              {(s.p ?? []).map((para, i) => (
                <p key={i} className="text-foreground/85 leading-relaxed mb-4">
                  <RichText text={para} />
                </p>
              ))}
              {Array.isArray(s.bullets) && s.bullets.length > 0 && (
                <ul className="list-disc pl-6 space-y-1 mb-4 text-foreground/85">
                  {s.bullets.map((b, i) => (
                    <li key={i}><RichText text={b} /></li>
                  ))}
                </ul>
              )}
              {s.table?.headers?.length ? (
                <div className="overflow-x-auto mb-4">
                  <table className="w-full text-sm border border-border rounded">
                    <thead className="bg-secondary/60">
                      <tr>{s.table.headers.map((h, i) => <th key={i} className="text-left p-2 border-b border-border font-semibold">{stripMarkdown(h)}</th>)}</tr>
                    </thead>
                    <tbody>
                      {s.table.rows.map((r, i) => (
                        <tr key={i} className="border-b border-border/60">
                          {r.map((c, j) => <td key={j} className="p-2 align-top"><RichText text={c} /></td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </section>
          ))}
          {post.body.faqs.length > 0 && (
            <section className="mt-12">
              <h2 className="font-display text-2xl md:text-3xl font-bold mb-5">FAQs</h2>
              <FAQAccordion items={post.body.faqs} idPrefix={post.slug} />
            </section>
          )}
        </div>
        <div className="mt-12 p-6 md:p-8 rounded-lg bg-secondary/60 border border-border">
          <h3 className="font-display text-2xl font-bold mb-2">Ready to plan your project?</h3>
          <p className="text-muted-foreground mb-5">Get a free in-home flooring estimate from VRP Flooring LLC.</p>
          <CTAGroup />
        </div>
        <div className="mt-12">
          <Link to="/blog" className="text-accentInk font-semibold hover:underline">← Back to all articles</Link>
        </div>
      </article>
    </Layout>
  );
};
export default BlogPost;
